import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold hover:shadow-lg hover:shadow-primary/25",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-lg hover:shadow-destructive/25",
        outline:
          "border border-border bg-background hover:bg-accent hover:text-accent-foreground shadow-sm",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Cyberpunk Variants
        neon: "bg-neon-cyan text-background font-semibold hover:shadow-lg hover:shadow-neon-cyan/50 hover:scale-105 transform transition-all duration-300 border border-neon-cyan/50",
        cyber: "bg-gradient-to-r from-electric-blue to-neon-cyan text-background font-bold hover:shadow-xl hover:shadow-neon-cyan/30 transform hover:scale-105",
        danger: "bg-neon-red text-background font-semibold hover:shadow-lg hover:shadow-neon-red/50 hover:scale-105 transform transition-all duration-300",
        glass: "glass-card border-accent/30 text-foreground font-semibold hover:border-accent hover:bg-accent/10 hover:shadow-lg hover:shadow-accent/25",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm font-semibold",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
